import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Calendar as CalendarIcon } from 'lucide-react';
import ReportCalendar from './report-calendar';

interface DateFieldProps {
  value: Date | null;
  onChange: (date: Date) => void;
  annotatedDates?: Date[];
  restrict?: boolean;
  placeholder?: string;
}

const DISPLAY_FORMAT = 'DD/MM/YYYY';

const DateField = ({
  value,
  onChange,
  annotatedDates = [],
  restrict = false,
  placeholder = 'DD/MM/AAAA',
}: DateFieldProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="rv-date-field">
      <div
        role="button"
        className="rv-date-field-box"
        tabIndex={0}
        onClick={() => setOpen((previous) => !previous)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') setOpen(true);
        }}
      >
        <span
          className={`rv-date-field-value${
            value ? '' : ' rv-date-field-value--empty'
          }`}
        >
          {value ? dayjs(value).format(DISPLAY_FORMAT) : placeholder}
        </span>
        <CalendarIcon className="rv-date-field-icon" size={18} />
      </div>

      {open ? (
        <div className="rv-date-field-dropdown">
          <ReportCalendar
            annotatedDates={annotatedDates}
            defaultMonth={value ?? undefined}
            selectableDates={restrict ? annotatedDates : undefined}
            value={value ?? undefined}
            onSelect={(date) => {
              onChange(date);
              setOpen(false);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default DateField;
